import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, FormStore, inject, Input, Modal, ModalHandle, TasksStore, Tooltip } from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { Button, ButtonGroup } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AppStore } from '../Core/AppStore';
import { UsersStore } from './UsersStore';
import { BsCheck } from '@react-icons/all-files/bs/BsCheck';
import { BsTrash } from '@react-icons/all-files/bs/BsTrash';
import { BsUnlock } from '@react-icons/all-files/bs/BsUnlock';
import { BsLock } from '@react-icons/all-files/bs/BsLock';
import { BsPencilSquare } from '@react-icons/all-files/bs/BsPencilSquare';

export interface IUserRowProps {
    readonly user: Mri.Web.Portal.Controllers.Account.UserModel;
}

@autoinject
@observer
export class UserRow extends React.Component<IUserRowProps> {
    public componentDidMount() {
        this.loadForm();
    }

    public render() {
        return (
            <tr>
                <td className="ps-2">{this.props.user.userName}</td>
                <td>
                    {this.props.user.email}
                    {this.props.user.emailConfirmed && <BsCheck />}
                </td>
                <td>{this.props.user.roles.join(', ')}</td>
                <td>{this.accountNames.join(', ')}</td>
                {this.accountStore.isInRoles(['MriAdmin']) && (
                    <td>
                        {this.userSettingsStore && (
                            <div className="form-switch">
                                <Input
                                    type="checkbox"
                                    className="form-check-input"
                                    field={this.userSettingsStore.fields.subscribedToAlerts}
                                    onChange={this.subscribeUserToAlerts}
                                />
                            </div>
                        )}
                    </td>
                )}
                <td>
                    <ButtonGroup className="py-1">
                        <Tooltip message="Edit User" delay={0}>
                            <LinkContainer to={`/users/${this.props.user.id}/edit`}>
                                <Button color="primary">
                                    <BsPencilSquare />
                                </Button>
                            </LinkContainer>
                        </Tooltip>

                        {this.isExactMatch && (
                            <Tooltip message="Delete User" delay={0}>
                                <Button
                                    className="dt-border"
                                    color="danger"
                                    onClick={this.delete}
                                    disabled={this.props.user.id == this.accountStore.user!.id}
                                >
                                    <BsTrash />
                                </Button>
                            </Tooltip>
                        )}

                        {!this.isExactMatch && (
                            <Tooltip message="Remove User" delay={0}>
                                <Button className="dt-border" color="danger" onClick={this.remove}>
                                    <BsTrash />
                                </Button>
                            </Tooltip>
                        )}

                        {this.props.user.lockedOut && (
                            <Tooltip message="Unlock User" delay={0}>
                                <Button id={'user-unlock-' + this.props.user.id} color="warning" onClick={this.unlock}>
                                    <BsLock />
                                </Button>
                            </Tooltip>
                        )}
                        {!this.props.user.lockedOut && (
                            <Tooltip message="Lock User" delay={0}>
                                <Button id={'user-lock-' + this.props.user.id} color="primary" onClick={this.lock}>
                                    <BsUnlock />
                                </Button>
                            </Tooltip>
                        )}
                    </ButtonGroup>
                </td>
                <Modal
                    className="modal-sm"
                    title="Confirm Deletion"
                    modalHandle={this.deleteModalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">{'Delete ' + this.props.user.userName + '?'}</div>
                </Modal>
                <Modal
                    className="modal-sm"
                    title="Confirm Remove"
                    modalHandle={this.removeModalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">{'Remove ' + this.props.user.userName + '?'}</div>
                </Modal>
            </tr>
        );
    }

    @bind
    @action
    private loadForm() {
        const user = this.props.user;

        if (user) {
            this.userSettingsStore = new FormStore<Mri.Web.Portal.Controllers.Manage.ChangeUserSettingsModel>({
                preferredTheme: user.preferredTheme,
                viewedChangelog: user.viewedChangelog,
                metaData: user.metaData,
                statusVuNewTab: user.statusVuNewTab,
                fahrenheitPreferred: user.fahrenheitPreferred,
                preferredTimezone: user.preferredTimezone,
                multiSortPreferred: user.multiSortPreferred,
                subscribedToAlerts: user.subscribedToAlerts,
            });
        }
    }

    @bind
    private async delete(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (await this.deleteModalHandle.confirm()) {
            const task = this.tasksStore.startSessionTask(`Delete ${this.props.user.userName}`);

            try {
                await this.usersStore.deleteAsync(this.props.user);
                task.complete();
            } catch (e: any) {
                task.error(e);
            }
        }
    }

    @bind
    private async remove(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (await this.removeModalHandle.confirm()) {
            const task = this.tasksStore.startSessionTask(`Remove ${this.props.user.userName}`);

            try {
                const accountsToRemove = this.props.user.accounts.filter(a =>
                    this.accountStore.user!.accounts.includes(a)
                );
                for (const account of accountsToRemove) {
                    await this.usersStore.removeAccountAsync(this.props.user, account);
                }
                this.usersStore.users.delete(this.props.user.id);
                task.complete();
            } catch (e: any) {
                task.error(e);
            }
        }
    }

    @bind
    private async unlock(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        const task = this.tasksStore.startSessionTask(`Unlock ${this.props.user.userName}`);

        try {
            this.usersStore.lockAsync(this.props.user, false);
            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    @bind
    private async lock(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        const task = this.tasksStore.startSessionTask(`Lock ${this.props.user.userName}`);

        try {
            this.usersStore.lockAsync(this.props.user, true);
            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    @bind
    private async subscribeUserToAlerts() {
        if (this.userSettingsStore) {
            const subscribed = this.userSettingsStore?.fields.subscribedToAlerts.value;
            const subscribedVerbage = subscribed ? 'Subscribe' : 'Unsubscribe';
            const task = this.tasksStore.startSessionTask(`${subscribedVerbage} ${this.props.user.userName}`);

            try {
                this.usersStore.subscribeUserToAlerts(this.props.user, subscribed);
                task.complete();
            } catch (e: any) {
                task.error(e);
            }
        }
    }

    @computed
    private get accountNames() {
        return this.props.user.accounts
            .map(a => this.accountsStore.accounts.get(a))
            .map(a => a?.accountName ?? 'Other Account');
    }

    @computed
    private get isExactMatch() {
        if (!this.accountStore.isLoggedIn) return false;
        if (this.accountStore.isInRoles(['MriAdmin'])) return true;
        if (!this.accountStore.isInRoles(['PortalAdmin'])) return false;

        return this.props.user.accounts.filter(a => !this.accountStore.user!.accounts.includes(a)).length == 0;
    }

    @observable
    private deleteModalHandle = new ModalHandle();
    @observable
    private removeModalHandle = new ModalHandle();
    @observable
    private userSettingsStore?: FormStore<Mri.Web.Portal.Controllers.Manage.ChangeUserSettingsModel>;
    @inject
    private usersStore!: UsersStore;
    @inject
    private accountStore!: AccountStore;
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private tasksStore!: TasksStore;
}
